/* Brents product page (sections/main-product-brents.liquid)
   - Dot pager for the media stack. Desktop: images scroll with the page, the active dot is the
     image nearest the viewport centre (Figma "Scroll Product View", node 289:7145). Below 990px
     the list is a horizontal snap slider and the dots follow its scroll position.
   - "Added" state on the Add to Bag button after a successful add (Figma node 267:1688); the
     bag drawer opens 700ms later so the confirmation is seen before the drawer covers it. */
(() => {
  class BrentsProductGallery {
    constructor(root) {
      this.root = root;
      this.dotContainer = root.querySelector('[data-brents-gallery-dots]');
      this.list = root.querySelector('.brents-product__gallery .product__media-list');
      this.items = [];
      this.dots = [];
      this.raf = null;

      if (!this.list) return;

      this.handleScroll = this.handleScroll.bind(this);

      this.build();
      this.list.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleScroll, { passive: true });

      new MutationObserver(() => window.requestAnimationFrame(() => this.build())).observe(this.list, {
        childList: true,
        subtree: false,
      });
    }

    getVisibleItems() {
      return Array.from(this.list.querySelectorAll('li[data-media-id]')).filter(
        (item) => window.getComputedStyle(item).display !== 'none'
      );
    }

    build() {
      this.items = this.getVisibleItems();
      if (!this.dotContainer) return;

      this.dotContainer.innerHTML = '';
      this.dots = [];

      if (this.items.length <= 1) {
        this.dotContainer.hidden = true;
        return;
      }
      this.dotContainer.hidden = false;

      this.items.forEach((item, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'brents-product__dot';
        button.setAttribute('aria-label', `View product image ${index + 1}`);
        button.addEventListener('click', () => this.scrollToItem(index));
        this.dotContainer.appendChild(button);
        this.dots.push(button);
      });

      this.updateActiveDot();
    }

    isMobileLayout() {
      return window.matchMedia('(max-width: 989px)').matches;
    }

    topBar() {
      const value = getComputedStyle(document.documentElement).getPropertyValue('--brents-top-bar');
      return parseFloat(value) * (value.trim().endsWith('rem') ? 10 : 1) || 0;
    }

    scrollToItem(index) {
      const item = this.items[index];
      if (!item) return;

      if (this.isMobileLayout()) {
        this.list.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
        return;
      }
      /* centre the image in the area below the sticky top bar */
      const rect = item.getBoundingClientRect();
      const bar = this.topBar();
      const target = window.scrollY + rect.top - bar - (window.innerHeight - bar - rect.height) / 2;
      window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    }

    getClosestIndex() {
      if (!this.items.length) return 0;

      if (this.isMobileLayout()) {
        const current = this.list.scrollLeft;
        let closest = 0;
        let distance = Infinity;
        this.items.forEach((item, index) => {
          const d = Math.abs(item.offsetLeft - current);
          if (d < distance) {
            distance = d;
            closest = index;
          }
        });
        return closest;
      }

      const bar = this.topBar();
      const centre = bar + (window.innerHeight - bar) / 2;
      let closest = 0;
      let distance = Infinity;
      this.items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const d = Math.abs(rect.top + rect.height / 2 - centre);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      return closest;
    }

    updateActiveDot() {
      const activeIndex = this.getClosestIndex();
      this.dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    handleScroll() {
      if (this.raf) return;
      this.raf = window.requestAnimationFrame(() => {
        this.updateActiveDot();
        this.raf = null;
      });
    }
  }

  /* Green "Added" flash on the buy button after Dawn's product-form adds to the cart */
  const initAddedState = (root) => {
    const button = root.querySelector('.brents-product__buy .product-form__submit');
    const form = root.querySelector('.brents-product__buy form');
    if (!button || !form || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    const label = button.querySelector('span');
    const addedText = root.dataset.addedLabel || 'Added';

    /* The drawer slides over the buy column, so opening it straight away hides the Added state.
       Dawn's product-form publishes cartUpdate synchronously and then calls
       cart.renderContents(), so the subscriber flags the next render and the wrapper holds it
       back long enough for the green button to read. Other callers (card quick-add) are untouched.
       The wrap happens here rather than at init: cart-drawer.js loads at the end of <body>, so
       the drawer element isn't upgraded yet (no renderContents) when this script first runs. */
    const delayNextCartRender = (ms) => {
      const cart = root.querySelector('.brents-product__buy product-form')?.cart;
      if (!cart || typeof cart.renderContents !== 'function') return;
      if (!cart.brentsDelayWrapped) {
        const renderContents = cart.renderContents.bind(cart);
        cart.renderContents = (...args) => {
          const delay = cart.brentsDelayNextRender;
          cart.brentsDelayNextRender = 0;
          if (delay) setTimeout(() => renderContents(...args), delay);
          else renderContents(...args);
        };
        cart.brentsDelayWrapped = true;
      }
      cart.brentsDelayNextRender = ms;
    };

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'product-form') return;
      const idInput = form.querySelector('[name="id"]');
      if (!idInput || String(event.productVariantId) !== String(idInput.value)) return;
      delayNextCartRender(700);
      const original = label ? label.textContent : '';
      button.classList.add('is-added');
      if (label) label.textContent = addedText;
      setTimeout(() => {
        button.classList.remove('is-added');
        if (label && label.textContent === addedText) label.textContent = original;
      }, 2000);
    });
  };

  /* Colour gallery (map rendered by sections/main-product-brents.liquid): the selected colour
     shows only its own photos, in Media order (client note 2026-09-29, "not mixed"). A photo tied
     to no colour shows for every colour, and a colour with no photos tied to it shows everything,
     so missing admin data never empties the gallery. Runs on load and on variantChange, which
     Dawn publishes after its own media update, so this wins; the dots are rebuilt afterwards. */
  const initColourGallery = (root, gallery) => {
    const el = root.querySelector('[data-colour-media]');
    const list = root.querySelector('.brents-product__gallery .product__media-list');
    if (!el || !list) return;
    let map;
    try {
      map = JSON.parse(el.textContent);
    } catch (e) {
      return;
    }
    const pos0 = parseInt(el.dataset.colourPosition, 10) - 1;
    const colours = map.colours || {};
    const order = map.order || [];
    const tied = new Set(Object.values(colours).flat());
    let current = null;

    const show = (colour) => {
      const ids = colours[colour] || [];
      const items = Array.from(list.querySelectorAll(':scope > li[data-media-id]'));
      const mediaId = (li) => li.dataset.mediaId.split('-').pop();
      const visible = (li) => !ids.length || ids.includes(mediaId(li)) || !tied.has(mediaId(li));
      const rank = (li) => {
        const k = order.indexOf(mediaId(li));
        return (visible(li) ? 0 : 1e6) + (k >= 0 ? k : 1e5);
      };

      const sorted = items.slice().sort((a, b) => rank(a) - rank(b));
      if (!sorted.every((li, i) => li === items[i])) sorted.forEach((li) => list.appendChild(li));
      sorted.forEach((li) => {
        li.classList.toggle('is-colour-hidden', !visible(li));
        li.classList.remove('is-colour-last');
      });
      sorted.filter(visible).pop()?.classList.add('is-colour-last');
      gallery?.build();

      if (colour === current) return;
      const first = current === null;
      current = colour;

      /* Back to photo one, and it has to be asserted more than once.

         Dawn renders the selected variant's photo first, so it is the one in view; the re-sort
         above moves it back to its Media position and the phone slider's scroll anchoring follows
         it, which opened the gallery on photo 3 of 9 (2026-10-05).

         Changing colour is worse, because Dawn's media gallery runs its OWN smooth
         scrollIntoView to the new variant's featured photo after it publishes variantChange. A
         smooth scroll keeps animating long after this handler returns, so it simply drove past
         the reset and parked on whichever photo the variant is tagged with — photo 4 of 7
         (reported 2026-10-06).

         So: reset instantly, then hold it. behavior 'instant' is required because
         component-slider.css puts scroll-behavior: smooth on .slider--mobile, which would
         otherwise animate this too and lose the same race. The repeats cover the next two frames
         (layout settling after the re-sort) and the window in which Dawn's smooth scroll is still
         running. 320ms is short enough that it cannot fight a real swipe. */
      const home = () => {
        try {
          list.scrollTo({ left: 0, behavior: 'instant' });
        } catch (e) {
          list.scrollLeft = 0;
        }
      };

      home();
      window.requestAnimationFrame(() => {
        home();
        window.requestAnimationFrame(home);
      });
      setTimeout(home, 150);
      setTimeout(home, 320);

      /* desktop stacks the photos down the page, so send the page back up to the gallery */
      if (!first && !window.matchMedia('(max-width: 989px)').matches && list.getBoundingClientRect().top < 0) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    show(el.dataset.currentColour);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant;
        if (variant && Array.isArray(variant.options)) show(variant.options[pos0]);
      });
    }
  };

  /* "Email me when available" (Figma Unavailable): toggle the inline form and keep the tagged
     variant in sync with Dawn's variant changes */
  const initNotify = (root) => {
    const wrap = root.querySelector('[data-notify]');
    if (!wrap) return;
    const toggle = wrap.querySelector('[data-notify-toggle]');
    const form = wrap.querySelector('[data-notify-form]');
    const tags = wrap.querySelector('[data-notify-tags]');
    const note = wrap.querySelector('[data-notify-note]');

    toggle?.addEventListener('click', () => {
      wrap.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      form.hidden = false;
      form.querySelector('input[type="email"]')?.focus();
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant;
        if (!variant || !tags) return;
        tags.value = `back-in-stock,variant:${variant.id}`;
        if (note) note.value = `Back in stock request: ${note.dataset.productTitle} / ${variant.title} (variant ${variant.id})`;
      });
    }
  };

  const init = (scope = document) => {
    scope.querySelectorAll('.brents-product').forEach((root) => {
      if (root.dataset.brentsGalleryInitialized === 'true') return;
      root.dataset.brentsGalleryInitialized = 'true';
      const gallery = new BrentsProductGallery(root);
      initAddedState(root);
      initNotify(root);
      initColourGallery(root, gallery);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('product-info:loaded', (event) => {
    const root = event.target.querySelector?.('.brents-product');
    if (root) init(root.parentNode);
  });
})();
